import { useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { useGroupsAndWorkspaceMembers } from "@dashboard/groups/store/helpers";
import { Theme } from "@mui/material";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import {
    MinimalTable,
    MinimalTableBody,
    MinimalTableContainer,
    MinimalTableHeader,
    MinimalTableHeaderCell,
    MinimalTableRow,
} from "@shared/table/MinimalTableComponents";
import CustomTablePagination from "@shared/table/TablePagination";

import { MemberListItem } from "./MemberListItem/MemberListItem";

const useStyles = makeStyles()((theme: Theme) => ({
    table: {
        flex: 1,
        minHeight: 0,
    },
    pagination: {
        marginTop: "auto",
        flexShrink: 0,
        borderTop: `1px solid ${theme.palette.emteria.border.secondary}`,
    },
}));

export const MembersTable = () => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const { t } = useTranslation(["common", "pages"]);
    const { classes } = useStyles();
    const allMembers = useGroupsAndWorkspaceMembers();

    const columns = [
        { id: "avatar", label: "" },
        { id: "email", label: t("common:label.email") },
        { id: "role", label: t("common:label.role") },
        { id: "actions", label: "" },
    ];

    const maxPage = Math.max(0, Math.ceil(allMembers.length / rowsPerPage) - 1);
    const currentPage = Math.min(page, maxPage);
    const pagedMembers = allMembers.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

    return (
        <SectionPanel disablePadding>
            <MinimalTableContainer plain className={classes.table}>
                <MinimalTable stickyHeader>
                    <MinimalTableHeader
                        title={{
                            label: t("pages:workspacePage.section.title.members"),
                            colSpan: columns.length,
                        }}
                    >
                        <MinimalTableRow>
                            {columns.map((column) => (
                                <MinimalTableHeaderCell key={column.id} align="left">
                                    {column.label}
                                </MinimalTableHeaderCell>
                            ))}
                        </MinimalTableRow>
                    </MinimalTableHeader>
                    <MinimalTableBody>
                        {pagedMembers.map((member, index) => (
                            <MemberListItem key={member.id} index={currentPage * rowsPerPage + index} member={member} />
                        ))}
                    </MinimalTableBody>
                </MinimalTable>
            </MinimalTableContainer>

            <CustomTablePagination
                component="div"
                className={classes.pagination}
                rowsPerPageOptions={[10, 25, 50]}
                count={allMembers.length}
                rowsPerPage={rowsPerPage}
                page={currentPage}
                onPageChange={(_event, nextPage) => setPage(nextPage)}
                onRowsPerPageChange={(event) => {
                    setRowsPerPage(Number(event.target.value));
                    setPage(0);
                }}
            />
        </SectionPanel>
    );
};
